export { default as debounce } from 'lodash/debounce.js';
export { default as throttle } from 'lodash/throttle.js';

// https://www.npmjs.com/package/memoizee
export { default as memoize } from 'memoizee';
export { default as memoizeeProfile } from 'memoizee/profile.js';
export { flow } from 'fp-ts/lib/function.js';

/**
 * Create a fake time delay in an async function useful for testing
 * @example
 * await delay(1000);
 */
export const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

// Types for the result object with discriminated union
type Success<T> = {
  data: T;
  error: null;
};

type Failure<E> = {
  data: null;
  error: E;
};

type Result<T, E = Error> = Success<T> | Failure<E>;

/**
 * Wraps a promise in a try/catch and returns a result object with the data or error
 *
 * As long as `strictNullChecks: true` then doing something like `if (error) throw error` is required to use `data`
 *
 * @example
 * const { data, error } = await tryCatch(() => someAsyncFunction({ id: 42 }));
 * if (error) {
 *   throw error;
 * }
 * data.info;
 */
export async function tryCatch<T, E = Error>(
  promise: Promise<T>,
): Promise<Result<T, E>> {
  try {
    const data = await promise;
    return { data, error: null };
  } catch (error) {
    return { data: null, error: error as E };
  }
}
